/**
 * Generate a stable external ID for a feature
 * Uses a combination of name and created_at to create a stable ID
 */
export const generateExternalId = (feature) => {
  // Use a combination of name (sanitized) and created_at timestamp
  const sanitizedName = feature.name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const timestamp = feature.created_at ? new Date(feature.created_at).getTime() : Date.now();
  return `${sanitizedName}-${timestamp}`;
};

/**
 * Determine feature type based on structure
 */
export const determineFeatureType = (feature) => {
  // If it has sub-features, it's a group
  if (feature.subFeatures && feature.subFeatures.length > 0) {
    return 'group';
  }
  // Could add logic here to determine epic vs feature
  // For now, default to 'feature'
  return 'feature';
};

/**
 * Extract metadata from feature
 * Maps portable fields like priority, effort, owner, customFields
 */
export const extractMetadata = (feature) => {
  const metadata = {};
  
  // Extract from details if it's a JSON object with metadata
  if (feature.details && typeof feature.details === 'object') {
    const details = feature.details;
    if (details.metadata) {
      if (details.metadata.priority !== undefined) {
        metadata.priority = details.metadata.priority;
      }
      if (details.metadata.effort !== undefined) {
        metadata.effort = details.metadata.effort;
      }
      if (details.metadata.owner) {
        metadata.owner = details.metadata.owner;
      }
      if (details.metadata.customFields) {
        metadata.customFields = details.metadata.customFields;
      }
    }
  }
  
  return Object.keys(metadata).length > 0 ? metadata : undefined;
};

/**
 * Convert a feature to PortableFeature format
 */
export const convertToPortableFeature = (feature, externalIdMap = new Map(), parentExternalId = null) => {
  const externalId = generateExternalId(feature);
  externalIdMap.set(feature.id, externalId);
  
  // Determine type based on whether it has subFeatures in the original structure
  // Note: at this point, subFeatures may have been removed, so we check the original
  const hasSubFeatures = feature.subFeatures && feature.subFeatures.length > 0;
  const featureType = hasSubFeatures ? 'group' : 'feature';
  
  const portableFeature = {
    externalId,
    name: feature.name,
    description: feature.description || '',
    parentExternalId: parentExternalId || undefined,
    position: feature.sort_order || 0,
    type: featureType,
    metadata: extractMetadata(feature),
    createdAt: feature.created_at || new Date().toISOString()
  };
  
  // Remove undefined metadata
  if (!portableFeature.metadata) {
    delete portableFeature.metadata;
  }
  
  return portableFeature;
};

/**
 * Recursively collect all features in a tree (including parent)
 */
export const collectAllFeatures = (feature, selectedChildren = null) => {
  const allFeatures = [];
  
  // Create a copy of the feature without subFeatures for the export
  const featureCopy = { ...feature };
  const originalSubFeatures = feature.subFeatures || [];
  
  // Add the parent feature (without subFeatures, we'll add them separately)
  allFeatures.push(featureCopy);
  
  // Add selected children if provided, otherwise add all children
  const childrenToProcess = selectedChildren !== null 
    ? originalSubFeatures.filter((_, index) => selectedChildren[index])
    : originalSubFeatures;
  
  // Recursively collect children
  childrenToProcess.forEach(child => {
    allFeatures.push(...collectAllFeatures(child));
  });
  
  return allFeatures;
};

/**
 * Export features to PortableFeature format
 */
export const exportFeatures = (features, selectedChildrenMap = new Map()) => {
  const externalIdMap = new Map(); // Maps internal ID to external ID
  const portableFeatures = [];
  
  // Build a map of all features for quick lookup
  const featureMap = new Map();
  const buildFeatureMap = (featureList) => {
    featureList.forEach(feature => {
      featureMap.set(feature.id, feature);
      if (feature.subFeatures && feature.subFeatures.length > 0) {
        buildFeatureMap(feature.subFeatures);
      }
    });
  };
  
  // Build the feature map first
  features.forEach(feature => {
    buildFeatureMap([feature]);
  });
  
  // Process each root feature
  features.forEach(feature => {
    const selectedChildren = selectedChildrenMap.get(feature.id);
    const allFeatures = collectAllFeatures(feature, selectedChildren);
    
    // Convert each feature to portable format
    allFeatures.forEach(f => {
      // Find parent external ID
      let parentExternalId = null;
      if (f.parent_id) {
        // First, check if we've already generated an external ID for the parent
        const parentExternalIdFromMap = externalIdMap.get(f.parent_id);
        if (parentExternalIdFromMap) {
          parentExternalId = parentExternalIdFromMap;
        } else {
          // Parent might be in the export but not yet processed
          // We'll handle this in the second pass
        }
      }
      
      const portable = convertToPortableFeature(f, externalIdMap, parentExternalId);
      portableFeatures.push(portable);
    });
  });
  
  // Second pass: Update parentExternalId references now that all external IDs are generated
  return portableFeatures.map(pf => {
    // Find the original feature to get its parent_id
    const originalFeature = Array.from(featureMap.values()).find(f => {
      const expectedExternalId = generateExternalId(f);
      return expectedExternalId === pf.externalId;
    });
    
    if (originalFeature && originalFeature.parent_id) {
      const parentExternalId = externalIdMap.get(originalFeature.parent_id);
      if (parentExternalId) {
        pf.parentExternalId = parentExternalId;
      }
    }
    
    return pf;
  });
};

/**
 * Download features as JSON file
 */
export const downloadFeaturesAsJSON = (portableFeatures, filename = null) => {
  const json = JSON.stringify(portableFeatures, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || `features-export-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

